import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Home as HomeIcon, ArrowLeft, Search, AlertTriangle, RefreshCw, Phone, Mail } from "lucide-react";

/**
 * Branded 404 / 500 error pages.
 * Variant: "404" or "500"
 */
export default function ErrorPage({ variant = "404" }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  const COPY = {
    "404": {
      es: {
        kicker: "Error 404",
        title: "Esta página se fue en un apagón",
        sub: "La dirección que buscas no existe o fue movida. Pero no te preocupes, tu energía sigue intacta.",
        primary: "Volver al inicio",
        secondary: "Buscar mi plan",
      },
      en: {
        kicker: "Error 404",
        title: "This page lost power",
        sub: "The address you're looking for doesn't exist or was moved. Don't worry — your energy is still on.",
        primary: "Back to home",
        secondary: "Find my plan",
      },
    },
    "500": {
      es: {
        kicker: "Error 500",
        title: "Algo se sobrecargó",
        sub: "Tuvimos un fallo temporal en nuestros servidores. Nuestro equipo ya fue notificado. Inténtalo de nuevo en unos segundos.",
        primary: "Reintentar",
        secondary: "Volver al inicio",
      },
      en: {
        kicker: "Error 500",
        title: "Something overloaded",
        sub: "We had a temporary glitch on our servers. Our team has been notified. Try again in a few seconds.",
        primary: "Retry",
        secondary: "Back to home",
      },
    },
  };
  const c = COPY[variant][lang];
  const isServer = variant === "500";

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid={`error-page-${variant}`}>
      <Helmet>
        <title>{c.title} — Velcor Energy</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Brand header */}
      <header className="px-6 md:px-10 py-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/velcor-logo.png" alt="" className="w-9 h-9 object-contain" />
          <div className="leading-tight">
            <div className="font-black text-velcor-ink text-[15.5px] tracking-tight">Velcor Energy</div>
            <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-velcor-green -mt-0.5">
              Texas
            </div>
          </div>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 md:px-6 py-10">
        <div className="w-full max-w-[640px] text-center">
          {/* Big number */}
          <div className="relative inline-block mb-6">
            <div
              className="text-velcor-ink leading-none tabular-nums select-none"
              style={{
                fontSize: "clamp(96px, 18vw, 200px)",
                fontWeight: 800,
                letterSpacing: "-0.06em",
                background: "linear-gradient(180deg, #0f1d3d 0%, #2f7a3a 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {variant}
            </div>
            <span className="absolute -top-2 -right-3 inline-grid place-items-center w-12 h-12 rounded-full bg-amber-50 ring-2 ring-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600" strokeWidth={2.4} />
            </span>
          </div>

          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-2">
            {c.kicker}
          </div>
          <h1
            className="display text-velcor-ink leading-[1.05] mb-4"
            style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 600, letterSpacing: "-0.025em" }}
          >
            {c.title}
          </h1>
          <p className="text-slate-600 text-[15px] leading-relaxed max-w-[520px] mx-auto mb-8">
            {c.sub}
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            {isServer ? (
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-velcor-green text-white font-extrabold text-[13px] hover:bg-velcor-green-dark transition-colors shadow-[0_8px_22px_rgba(47,122,58,0.28)]"
                data-testid="error-primary"
              >
                <RefreshCw className="w-4 h-4" />
                {c.primary}
              </button>
            ) : (
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-velcor-green text-white font-extrabold text-[13px] hover:bg-velcor-green-dark transition-colors shadow-[0_8px_22px_rgba(47,122,58,0.28)]"
                data-testid="error-primary"
              >
                <HomeIcon className="w-4 h-4" />
                {c.primary}
              </Link>
            )}
            <Link
              to={isServer ? "/" : "/shop"}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border-2 border-slate-200 hover:border-velcor-green hover:text-velcor-green text-velcor-ink font-bold text-[13px] transition-all"
              data-testid="error-secondary"
            >
              {isServer ? <ArrowLeft className="w-4 h-4" /> : <Search className="w-4 h-4" />}
              {c.secondary}
            </Link>
          </div>

          {/* Help links */}
          <div className="mt-10 pt-8 border-t border-slate-100 grid sm:grid-cols-2 gap-3 text-[12.5px] text-slate-500">
            <a href="tel:+18007156516" className="inline-flex items-center justify-center gap-2 hover:text-velcor-green transition-colors">
              <Phone className="w-3.5 h-3.5" /> (800) 715-6516
            </a>
            <a href="mailto:support@velcorenergy.com" className="inline-flex items-center justify-center gap-2 hover:text-velcor-green transition-colors">
              <Mail className="w-3.5 h-3.5" /> support@velcorenergy.com
            </a>
          </div>
        </div>
      </main>

      <footer className="px-6 py-6 text-center text-[11.5px] text-slate-400">
        © {new Date().getFullYear()} Velcor Energy LLC · PUCT REP# Pending · Houston, TX
      </footer>
    </div>
  );
}
